'use client';

import React, { useEffect } from 'react';
import injection from '@apgnext/project/legacy/js/injection';
import { usePathname } from 'next/navigation';
import dot from 'dot-object';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import app from './app/app';
import Notifications from '@frontastic/catwalk/js/component/notifications';
import AppContainer from './appContainer';
import { store } from '../../redux/store';
import { useFrontasticBridgeReduxDispatches } from '../../redux/useFrontasticBridgeReduxDispatches';
import { PageResponse } from '@commercetools/frontend-sdk/lib/types/api/page/PageResponse';
import { useUrl } from '@apgnext/frontend/providers/url';

import '../scss/app.scss';

// we have to call this function so the import won't be optimized away.
injection();

type DotInput = {
  [key in string]: string | DotInput;
};

type Props = React.PropsWithChildren<{
  pageData: PageResponse;
  messages: DotInput;
}>;

const CatwalkProviderImpl: React.FunctionComponent<Props> = ({ children, pageData, messages }) => {
  const pathname = usePathname();

  const context = store.getState().app.context;
  const locale = context.locale;
  const language = locale.split('_')[0];
  const { hostname } = useUrl();

  useFrontasticBridgeReduxDispatches(pageData);

  useEffect(() => {
    app.loadForLocation(window.location);
  }, [pathname]);

  const pageFolderType = pageData.pageFolder?.pageFolderType || '';
  const customClassname = pageData.pageFolder?.configuration?.displayClassname || '';

  return (
    <Provider store={store}>
      <IntlProvider locale={language} messages={dot.dot(messages[locale] || {})} textComponent="span">
        <AppContainer>
          <div className={`s-node s-node--${pageFolderType} ${customClassname}`}>{children}</div>
          <Notifications />
        </AppContainer>
      </IntlProvider>
    </Provider>
  );
};

export const CatwalkProvider: React.FunctionComponent<Props> = ({ children, pageData, messages }) => {
  if (
    !pageData ||
    typeof pageData === 'string' ||
    ('ok' in pageData && 'message' in pageData && !pageData.ok && pageData.message)
  ) {
    return <>{children}</>;
  }

  return (
    <CatwalkProviderImpl pageData={pageData} messages={messages}>
      {children}
    </CatwalkProviderImpl>
  );
};
